import { useAmplifyAuth } from "$src/hooks/use-amplify-auth";
import { useNav } from "$src/hooks/use-nav";
import { cx } from "$src/lib/utils";
import { isEarlyMoments } from "$src/lib/teams";
import { useAccount } from "$src/stores/use-account";
import { IconButton } from "$src/components/icon-button/icon-button";
import { useBrandContextPanel } from "$src/stores/use-brand-context-panel";
import { useCreatePageReset } from "$src/stores/use-create-page-reset";
import { media } from "$src/styles";
import { NotebookPen } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useMediaQuery } from "usehooks-ts";

import { BrandDropdown } from "../brand-dropdown/brand-dropdown";
import { SettingsDropdown } from "../settings-dropdown/settings-dropdown";
import { MonthSelector } from "../month-selector/month-selector";
import { MiniChatButton } from "../mini-chat-button/mini-chat-button";
import { SplitAccountNav } from "../split-account-nav/split-account-nav";
import styles from "./header.module.css";

export const Header = () => {
  const isDesktop = useMediaQuery(media.desktop);
  const { pathname } = useLocation();
  const account = useAccount((s) => s.active);
  const nav = useNav();
  const { session } = useAmplifyAuth();
  const openMilestones = useBrandContextPanel((s) => s.openMilestones);
  const bumpCreateReset = useCreatePageReset((s) => s.bump);
  const [mobileOpen, setMobileOpen] = useState(false);

  const teamSlug = pathname.split("/")[1] || "";
  const isVision = pathname.startsWith(`/${teamSlug}/vision`);
  const showMonthSelector = isEarlyMoments(teamSlug) && !isVision;
  const [brandModalOpen, setBrandModalOpen] = useState(false);

  useEffect(() => {
    mobileOpen && isDesktop && setMobileOpen(false);
  }, [isDesktop, mobileOpen]);

  return (
    <header className={cx(styles.header)}>
      {isDesktop ? (
        <>
          <div className={styles.leftSection}>
            <Link className={styles.logo} to="/">
              <img
                src="/tracksuit-logo.png"
                alt="Tracksuit"
                className={styles["logo-inner"]}
              />
            </Link>
            {isVision && (
              <button
                type="button"
                className={styles.secretHotspot}
                onClick={() => setBrandModalOpen(true)}
                aria-label="Change brand"
                title="Change brand"
              />
            )}
            {isVision && (
              <nav className={styles.nav}>
                {nav.map(({ label, path, badge, exact }) => (
                  <Link
                    className={cx(
                      styles.link,
                      (exact ? pathname === path : pathname.startsWith(path)) && styles.active,
                    )}
                    to={path}
                    key={path}
                    onClick={() => {
                      // Clicking the "Create" nav (vision/plan) always returns
                      // the page to its default state — even when already on it.
                      if (path.endsWith("/vision/plan")) bumpCreateReset();
                    }}
                  >
                    {label}
                    {badge && <span className={styles.linkBadge}>{badge}</span>}
                  </Link>
                ))}
              </nav>
            )}
            {showMonthSelector && <MonthSelector />}
          </div>
          {!isVision && (
            <nav className={styles.nav}>
              {nav.map(({ label, path, badge, exact }) => (
                <Link
                  className={cx(
                    styles.link,
                    (exact ? pathname === path : pathname.startsWith(path)) && styles.active,
                  )}
                  to={path}
                  key={path}
                >
                  {label}
                  {badge && <span className={styles.linkBadge}>{badge}</span>}
                </Link>
              ))}
            </nav>
          )}
          {!!session && (
            <div className={styles.actions}>
              <MiniChatButton />
              <IconButton
                icon={NotebookPen}
                tip="Brand context"
                large
                buttonProps={{
                  onClick: openMilestones,
                  style: {
                    "--icon-button-color": "var(--color-purple-200)",
                    "--icon-button-hover-color": "var(--color-off-white)",
                  } as React.CSSProperties,
                }}
              />
              <SettingsDropdown />
              {account &&
                (isVision ? (
                  <>
                    <SplitAccountNav />
                    <BrandDropdown
                      open={brandModalOpen}
                      onOpenChange={setBrandModalOpen}
                      hideTrigger
                    />
                  </>
                ) : (
                  <BrandDropdown />
                ))}
            </div>
          )}
        </>
      ) : (
        <>
          <Link className={styles.logo} to="/">
            <span className={styles["logo-inner"]}>Tracksuit</span>
          </Link>
          {session && (
            <div className={styles.actions}>
              <SettingsDropdown />
              <BrandDropdown />
            </div>
          )}
        </>
      )}
    </header>
  );
};
